// storyDiff — which top-level blocks of a story board changed since it was
// last saved (#1578). Used only to draw an "Unsaved" mark on the blocks that
// hold unsaved edits; the save itself always writes the whole document (see
// StoryEditor.tsx and the CLAUDE.md note that a partial save would mint
// versions never seen on screen).
//
// An element's outerSource differs from its ancestor's whenever ANY
// descendant's bytes differ — an ancestor's byte range always includes its
// descendants'. So, for this document's tree shape, the outermost element
// whose content changed is always its top-level block (hero, section or
// sources): nothing deeper can be "outermost" while an ancestor above it
// still differs too. `changedElements` reports exactly those ordinals.

import { outerSource, parseStory, type StoryDoc } from './storySource.js'

function sameShape(a: StoryDoc, b: StoryDoc): boolean {
  if (a.nodes.length !== b.nodes.length) return false
  return a.nodes.every((n, i) => n.tag === b.nodes[i].tag)
}

/**
 * The top-level blocks that changed between two versions of a story's
 * source, reported as PRESENT's ordinals. When the two parse to the same
 * shape (same element count and tags, in order — true of every ordinary
 * edit: typing, an attribute, a hide or show), a block is changed when its
 * own bytes differ from the saved version's block at the same ordinal.
 * Otherwise (a section moved, duplicated or deleted — ordinals no longer
 * line up 1:1), a block is changed when its exact bytes are not found
 * anywhere in `saved`: a block that only moved, or a copy of one that
 * already existed, keeps its clean state.
 */
export function changedElements(saved: string, present: string): number[] {
  if (saved === present) return []
  const savedDoc = parseStory(saved)
  const doc = parseStory(present)
  if (sameShape(savedDoc, doc)) {
    return doc.topLevel.filter((idx) => outerSource(doc, idx) !== outerSource(savedDoc, idx))
  }
  return doc.topLevel.filter((idx) => !saved.includes(outerSource(doc, idx)))
}
